import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { LuTrash2 } from 'react-icons/lu';
import {
  deleteVendorInvoiceVendor,
  fetchVendorList,
} from '../../utils/agentsApi';
import {
  VENDOR_INVOICE_QUERY_KEYS,
  VENDOR_INVOICE_ROUTES,
} from './vendorInvoice/utils';

const pageStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  paddingBottom: '2rem',
};

const sectionTitleStyle = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: 'var(--black)',
};

const emptyStyle = {
  color: 'var(--darkGrey)',
  fontSize: '0.9rem',
};

const btnPrimary = {
  border: '1px solid var(--lighterGrey)',
  borderRadius: '0.35rem',
  padding: '0.45rem 0.9rem',
  fontSize: '0.875rem',
  cursor: 'pointer',
  background: 'var(--lightBlue)',
  borderColor: 'var(--lightBlue)',
  color: 'var(--white)',
};

const VENDOR_COLS = '14rem 5.5rem 10rem 8rem minmax(12rem, 1fr)';
const DELETE_COL_WIDTH = '2.25rem';

const listHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0',
  borderBottom: '1px solid var(--lighterGrey)',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--darkGrey)',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const listRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.65rem 0',
  borderBottom: '1px solid var(--lighterGrey)',
  cursor: 'pointer',
};

const colsGridStyle = {
  flex: 1,
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: VENDOR_COLS,
  columnGap: '0.75rem',
  alignItems: 'center',
};

const deleteBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: DELETE_COL_WIDTH,
  flexShrink: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--darkGrey)',
  padding: '0.25rem',
  borderRadius: '0.25rem',
};

const typeBadgeStyle = {
  display: 'inline-block',
  padding: '0.1rem 0.45rem',
  borderRadius: '0.25rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  background: 'var(--lightestGrey)',
  color: 'var(--darkGrey)',
  textTransform: 'uppercase',
};

const VendorConfiguration = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: VENDOR_INVOICE_QUERY_KEYS.vendors,
    queryFn: fetchVendorList,
    refetchOnWindowFocus: true,
  });

  const vendors = data?.vendors ?? [];

  const deleteMutation = useMutation({
    mutationFn: (name) => deleteVendorInvoiceVendor(name),
    onSuccess: () => {
      toast.success('Vendor deleted.');
      queryClient.invalidateQueries({
        queryKey: VENDOR_INVOICE_QUERY_KEYS.vendors,
      });
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to delete vendor.');
    },
  });

  const handleDelete = (e, name) => {
    e.stopPropagation();
    if (deleteMutation.isPending) {
      return;
    }
    if (
      !window.confirm(
        `Delete vendor "${name}"? Existing invoice jobs are not affected.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(name);
  };

  return (
    <div className="container scrollable" style={pageStyle}>
      <p className="title">Vendor Configuration</p>
      <p className="sub-title">
        Manage invoice vendors. Open a vendor to edit configuration.
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <button
          type="button"
          style={btnPrimary}
          onClick={() => navigate(`${VENDOR_INVOICE_ROUTES.VENDORS}/new`)}
        >
          Add vendor
        </button>
      </div>

      <section
        style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
      >
        <h2 style={sectionTitleStyle}>
          Vendors
          {(isLoading || isFetching) && !vendors.length ? '…' : ''}
        </h2>
        {isLoading && !vendors.length && (
          <p style={emptyStyle}>Loading vendors…</p>
        )}
        {!isLoading && vendors.length === 0 && (
          <p style={emptyStyle}>No vendors configured yet.</p>
        )}
        {vendors.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '48rem' }}>
              <div style={listHeaderStyle}>
                <div
                  style={{ width: DELETE_COL_WIDTH, flexShrink: 0 }}
                  aria-hidden
                />
                <div style={colsGridStyle}>
                  <span>Name</span>
                  <span>Type</span>
                  <span>Supplier HCC ID</span>
                  <span>Reason code</span>
                  <span>Details</span>
                </div>
              </div>
              {vendors.map((vendor) => (
                <div
                  key={vendor.name}
                  role="button"
                  tabIndex={0}
                  style={listRowStyle}
                  onClick={() =>
                    navigate(
                      `${VENDOR_INVOICE_ROUTES.VENDORS}/${encodeURIComponent(vendor.name)}`,
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(
                        `${VENDOR_INVOICE_ROUTES.VENDORS}/${encodeURIComponent(vendor.name)}`,
                      );
                    }
                  }}
                >
                  <button
                    type="button"
                    style={{
                      ...deleteBtnStyle,
                      cursor: deleteMutation.isPending
                        ? 'not-allowed'
                        : 'pointer',
                      opacity: deleteMutation.isPending ? 0.5 : 1,
                    }}
                    onClick={(e) => handleDelete(e, vendor.name)}
                    disabled={deleteMutation.isPending}
                    aria-label={`Delete ${vendor.name}`}
                  >
                    <LuTrash2 />
                  </button>
                  <div style={colsGridStyle}>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {vendor.name}
                    </span>
                    <span>
                      <span style={typeBadgeStyle}>Invoice</span>
                    </span>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {vendor.supplierHccID || '—'}
                    </span>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {vendor.reasonCode || '—'}
                    </span>
                    <span
                      style={{ fontSize: '0.85rem', color: 'var(--darkGrey)' }}
                    >
                      —
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default VendorConfiguration;
